import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

interface FlagIconProps {
  code: string;
  className?: string;
}

function FlagIcon({ code, className }: FlagIconProps) {
  if (code === 'en') {
    return (
      <svg
        viewBox="0 0 60 30"
        className={className}
        role="img"
        aria-label="United Kingdom flag"
      >
        <clipPath id="s">
          <path d="M0,0 v30 h60 v-30 z" />
        </clipPath>
        <clipPath id="t">
          <path d="M30,15 h30 v15 z v-15 h-30 z h-30 v15 z v-15 h30 z" />
        </clipPath>
        <g clipPath="url(#s)">
          <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
          <path
            d="M0,0 L60,30 M60,0 L0,30"
            clipPath="url(#t)"
            stroke="#C8102E"
            strokeWidth="4"
          />
          <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </g>
      </svg>
    );
  }

  if (code === 'el') {
    return (
      <svg
        viewBox="0 0 27 18"
        className={className}
        role="img"
        aria-label="Greek flag"
      >
        <rect width="27" height="18" fill="#0D5EAF" />
        <g fill="#fff">
          <rect width="27" height="2" y="0" />
          <rect width="27" height="2" y="4" />
          <rect width="27" height="2" y="8" />
          <rect width="27" height="2" y="12" />
          <rect width="27" height="2" y="16" />
          <rect width="10" height="10" x="0" y="0" fill="#0D5EAF" />
          <rect width="10" height="2" x="0" y="4" fill="#fff" />
          <rect width="2" height="10" x="4" y="0" fill="#fff" />
        </g>
      </svg>
    );
  }

  return null;
}

export function LanguageSelector() {
  const { t } = useTranslation();
  const { language, changeLanguage, availableLanguages } = useLanguage();
  const [changing, setChanging] = useState(false);

  const handleLanguageToggle = async () => {
    const newLanguage = language === 'en' ? 'el' : 'en';

    setChanging(true);
    try {
      await changeLanguage(newLanguage);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setChanging(false);
    }
  };

  const oppositeLanguage = availableLanguages.find((lang) => lang.code !== language);

  if (!oppositeLanguage) return null;

  return (
    <button
      onClick={handleLanguageToggle}
      disabled={changing}
      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors group disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-400/50"
      aria-label={`${t('common:language.select')}: ${oppositeLanguage.nativeName}`}
      title={`Switch to ${oppositeLanguage.nativeName}`}
    >
      <div className="w-6 h-4 rounded-sm overflow-hidden border border-white/20 shadow-sm flex-shrink-0">
        <FlagIcon code={oppositeLanguage.code} className="w-full h-full" />
      </div>
      <span className="text-sm font-medium text-white/70 group-hover:text-white hidden sm:inline">
        {oppositeLanguage.nativeName}
      </span>
    </button>
  );
}
